import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { baseXUnit, color, font, fontSize, spacing } from '../../styles/theme';
import H from '../elements/H';
import { t } from '../../utils/i18n';
import Spacing from '../common/Spacing';
import IconText from '../common/IconText';
import { IconName } from '../common/Icon';
import Paragraph from '../elements/Paragraph';
import Row from '../grid/Row';
import Col from '../grid/Col';
import {
  selectSiteSettings,
  siteSettingsActions
} from '../../redux/features/siteSettings';
import RenderAsync from '../common/RenderAsync';
import HyperLink from '../elements/HyperLink';
import { SUPPORT_URL, SUPPORT_URL_LABEL } from '../../urls/supportUrl';

interface ContactLineProps {
  icon: IconName;
  title: string;
  contactDetail: string;
  url: string;
  external?: boolean;
}

interface CustomerSupportInfoProps {
  supportOfficeHours: string;
  emergencyPhoneNumber: string;
}

const ContactLine: React.FunctionComponent<ContactLineProps> = ({
  icon,
  title,
  contactDetail,
  url,
  external
}) => {
  return (
    <>
      <Spacing left={[4, 5, 5, 5]}>
        <H styling="h5" color="gray" data>
          {title}
        </H>
      </Spacing>

      <HyperLink
        href={url}
        data-testid="contact-line-link"
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        <IconText icon={icon} iconSize={[3, 4, 4]} iconColor="purple-light">
          <span className="contact-detail">{contactDetail}</span>
        </IconText>
      </HyperLink>

      {/*language=CSS*/}
      <style jsx>{`
        .contact-detail {
          font-size: ${fontSize.px18};
          font-family: ${font.mono};
        }
      `}</style>
    </>
  );
};

const CustomerSupportInfo: React.FunctionComponent<
  CustomerSupportInfoProps
> = ({ supportOfficeHours, emergencyPhoneNumber }) => {
  return (
    <IconText icon="travel-question" iconSize={2} iconColor="gray">
      <Paragraph small>
        <div className="customer-support-info">
          {t('Asiakaspalvelumme palvelee arkisin klo ')}
          {supportOfficeHours}
          {'. '}
          {t(
            'Muina vuorokauden aikoina Ikaalisten Matkatoimiston puhelinnumero poikkeus- ja hätätapauksia varten on '
          )}
          <HyperLink
            href={'tel:' + emergencyPhoneNumber}
            className="link"
            data-testid="emergency-link"
            linkColor="purple-light"
          >
            {emergencyPhoneNumber}
          </HyperLink>
          {'. '}
          {t('Puhelun hinta 2,96 € + pvm.')}
        </div>
      </Paragraph>
      {/*language=CSS*/}
      <style jsx>{`
        .customer-support-info {
          padding-top: 2px;
        }
      `}</style>
    </IconText>
  );
};

const ContactCard: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(siteSettingsActions.fetchSiteSettingsIfNotFetched());
  }, [dispatch]);
  const siteSettingsLoadable = useSelector(selectSiteSettings);

  return (
    <div className="contact-card" data-testid="contact-card">
      <RenderAsync
        state={siteSettingsLoadable && siteSettingsLoadable.loadingState}
      >
        {() => (
          <Spacing left={[2, 2, 4]} right={[2, 2, 4]}>
            <Row>
              <Col size={24}>
                <Spacing top={[1, 1, 2]} bottom={3}>
                  <H styling="h2">
                    <strong>{t('IMT.fi asiakaspalvelu')}</strong>
                  </H>
                </Spacing>
              </Col>
            </Row>
            <Row>
              <Col size={[24, 12, 12]}>
                <ContactLine
                  icon="travel-internet"
                  title={t('Asiakaspalvelu verkossa')}
                  contactDetail={SUPPORT_URL_LABEL}
                  url={SUPPORT_URL}
                  external
                />
                <Spacing top={3} bottom={4}>
                  <ContactLine
                    icon="contact-customer-service"
                    title={t('Soita meille')}
                    contactDetail={
                      siteSettingsLoadable.settings.supportPhoneNumber
                    }
                    url={
                      'tel:' + siteSettingsLoadable.settings.supportPhoneNumber
                    }
                  />
                </Spacing>
              </Col>
              <Col size={[24, 12, 12]}>
                <Spacing left={[1, 0, 0, 0]} bottom={1}>
                  <CustomerSupportInfo
                    supportOfficeHours={
                      siteSettingsLoadable.settings.supportOfficeHours
                    }
                    emergencyPhoneNumber={
                      siteSettingsLoadable.settings.emergencyPhoneNumber
                    }
                  />
                </Spacing>
              </Col>
            </Row>
          </Spacing>
        )}
      </RenderAsync>
      {/*language=CSS*/}
      <style jsx>{`
        .contact-card {
          border-radius: ${baseXUnit(1)};
          box-shadow: 0 2px ${baseXUnit(2)} 0 rgba(0, 0, 0, 0.15);
          padding-top: ${spacing.two};
          padding-bottom: ${spacing.two};
        }
        .customer-support-info {
          padding-top: 2px;
        }
        .link {
          color: ${color.purple};
        }
      `}</style>
    </div>
  );
};

export default ContactCard;
